import React, { useState, useMemo, useRef } from 'react'
import { Plus, Calendar, Database, FileText, User } from 'lucide-react'
import {
  DYNAMIC_DATE_VARIABLES,
  DYNAMIC_DATE_FORMAT_PRESETS,
  DEFAULT_DYNAMIC_DATE_FORMAT,
  getDynamicDateVariablesByCategory,
  containsDynamicDateVariables,
  previewDynamicDateText,
  isValidDynamicDateVariable,
  normalizeDynamicDateFormatPreset,
} from '@/lib/dateUtils'
import { WORKFLOW_SYSTEM_VARIABLES } from '@/lib/workflowSystemVariables'

/**
 * Enhanced VariableReference Component
 * 
 * Supports both extracted variables ({{variable}}) and dynamic date variables (~variable~)
 */
const EnhancedVariableReference = ({ 
  value, 
  onChange, 
  onBlur,
  availableVariables = [], 
  placeholder = "Enter text or use variables...",
  title,
  className = "",
  disabled = false,
  multiline = false,
  rows = 2,
  showDateVariables = true,
  showSystemVariables = true,
  systemVariables = WORKFLOW_SYSTEM_VARIABLES,
  showPreview = true,
  dateFormat = DEFAULT_DYNAMIC_DATE_FORMAT,
  onDateFormatChange = null,
}) => {
  // Ensure value is never null or undefined
  const safeValue = value === null || value === undefined ? "" : String(value)
  
  const [showVariableMenu, setShowVariableMenu] = useState(false)
  const [activeTab, setActiveTab] = useState('extracted') // 'extracted', 'dates', or 'form'
  const [searchTerm, setSearchTerm] = useState('')
  const [cursorPosition, setCursorPosition] = useState(0)
  const inputRef = useRef(null)
  const menuRef = useRef(null)
  const [localValue, setLocalValue] = useState(safeValue)
  const [isFocused, setIsFocused] = useState(false)
  const [isVariableButtonClicked, setIsVariableButtonClicked] = useState(false)

  // Sync localValue from prop ONLY when not focused and menu closed
  React.useEffect(() => {
    if (!isFocused && !showVariableMenu && safeValue !== localValue) {
      setLocalValue(safeValue)
    }
  }, [safeValue, isFocused, showVariableMenu, localValue])

  // Separate form fields from other variables
  const { extractedVariables, formFields } = useMemo(() => {
    const extracted = []
    const form = []
    
    availableVariables.forEach(variable => {
      if (variable.type === 'form_field') {
        form.push(variable)
      } else {
        extracted.push(variable)
      }
    })
    
    return { extractedVariables: extracted, formFields: form }
  }, [availableVariables])

  // Deduplicate extracted variables by name and filter by search term
  const filteredExtractedVariables = useMemo(() => {
    const uniqueVariables = new Map()
    extractedVariables.forEach(variable => {
      if (!uniqueVariables.has(variable.name)) {
        uniqueVariables.set(variable.name, variable)
      }
    })
    return Array.from(uniqueVariables.values())
      .filter(variable => 
        variable.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [extractedVariables, searchTerm])

  // Filter and group form fields by search term and form source
  const filteredFormFields = useMemo(() => {
    const filtered = formFields
      .filter(field => {
        const term = searchTerm.toLowerCase()
        return (
          field.name.toLowerCase().includes(term) ||
          (field.label && field.label.toLowerCase().includes(term)) ||
          (field.source && field.source.toLowerCase().includes(term))
        )
      })
    
    // Group by form source
    const grouped = {}
    filtered.forEach(field => {
      const source = field.source || 'Unknown Form'
      if (!grouped[source]) {
        grouped[source] = []
      }
      grouped[source].push(field)
    })
    
    // Sort fields within each group by label, then token name
    Object.keys(grouped).forEach(source => {
      grouped[source].sort((a, b) => {
        const labelA = (a.label || a.name).toLowerCase()
        const labelB = (b.label || b.name).toLowerCase()
        if (labelA !== labelB) return labelA.localeCompare(labelB)
        return a.name.localeCompare(b.name)
      })
    })
    
    return grouped
  }, [formFields, searchTerm])

  // Filter dynamic date variables by search term
  const filteredDateCategories = useMemo(() => {
    const categories = getDynamicDateVariablesByCategory()
    if (!searchTerm) return categories

    const filtered = {}
    Object.keys(categories).forEach(categoryKey => {
      const filteredVariables = categories[categoryKey].variables.filter(variable => 
        variable.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        variable.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        variable.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
      if (filteredVariables.length > 0) {
        filtered[categoryKey] = {
          ...categories[categoryKey],
          variables: filteredVariables
        }
      }
    })
    return filtered
  }, [searchTerm])

  const hasSystemVariables = showSystemVariables && systemVariables.length > 0

  const filteredSystemVariablesByGroup = useMemo(() => {
    const term = searchTerm.toLowerCase()
    const filtered = systemVariables.filter(variable =>
      variable.name.toLowerCase().includes(term) ||
      variable.description.toLowerCase().includes(term) ||
      (variable.group && variable.group.toLowerCase().includes(term))
    )

    const grouped = {}
    filtered.forEach(variable => {
      const group = variable.group || 'System'
      if (!grouped[group]) {
        grouped[group] = []
      }
      grouped[group].push(variable)
    })

    return grouped
  }, [searchTerm, systemVariables, hasSystemVariables])

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the menu AND outside the input/textarea
      const isOutsideMenu = menuRef.current && !menuRef.current.contains(event.target)
      const isOutsideInput = inputRef.current && !inputRef.current.contains(event.target)
      
      if (isOutsideMenu && isOutsideInput) {
        setShowVariableMenu(false)
        setSearchTerm('')
      }
    }

    if (showVariableMenu) {
      // Use both click and mousedown to ensure we catch the event
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('mousedown', handleClickOutside)
      
      return () => {
        document.removeEventListener('click', handleClickOutside)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showVariableMenu])

  const insertExtractedVariable = (variableName) => {
    const variableRef = `{{${variableName}}} `
    const before = localValue || ''
    // Always get the current cursor position from the input element
    const currentPosition = inputRef.current ? inputRef.current.selectionStart || 0 : 0
    const newValue = before.slice(0, currentPosition) + variableRef + before.slice(currentPosition)
    setLocalValue(newValue)
    onChange(newValue)
    setShowVariableMenu(false)
    setSearchTerm('')
    // Set cursor position after the inserted variable and space
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPosition = currentPosition + variableRef.length
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
        inputRef.current.focus()
      }
    }, 0)
  }

  const insertDateVariable = (variableKey) => {
    if (!isValidDynamicDateVariable(variableKey)) return
    const variableRef = `~${variableKey}~ `
    const before = localValue || ''
    // Always get the current cursor position from the input element
    const currentPosition = inputRef.current ? inputRef.current.selectionStart || 0 : 0
    const newValue = before.slice(0, currentPosition) + variableRef + before.slice(currentPosition)
    setLocalValue(newValue)
    onChange(newValue)
    setShowVariableMenu(false)
    setSearchTerm('')
    // Set cursor position after the inserted variable and space
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPosition = currentPosition + variableRef.length
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
        inputRef.current.focus()
      }
    }, 0)
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setLocalValue(newValue)
    onChange(newValue)
  }

  const handleKeyDown = (e) => {
    if (e.key === '{' && e.ctrlKey) {
      e.preventDefault()
      setActiveTab('extracted')
      setShowVariableMenu(!showVariableMenu)
    } else if (e.key === '~' && e.ctrlKey) {
      e.preventDefault()
      setActiveTab('dates')
      setShowVariableMenu(!showVariableMenu)
    } else if (e.key === 'f' && e.ctrlKey) {
      e.preventDefault()
      setActiveTab('form')
      setShowVariableMenu(!showVariableMenu)
    }
  }

  const handleInputSelect = (e) => {
    setCursorPosition(e.target.selectionStart || 0)
  }

  const handleInputClick = (e) => {
    setCursorPosition(e.target.selectionStart || 0)
  }

  const handleVariableButtonClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Mark that the variable button was clicked to prevent onBlur from running
    setIsVariableButtonClicked(true)
    
    // Toggle menu state
    setShowVariableMenu(!showVariableMenu)
    
    // Keep the input focused
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 10)
  }

  const handleInputBlur = (e) => {
    // Check if the blur is caused by clicking the variable button
    const isBlurFromVariableButton = e.relatedTarget && 
      e.relatedTarget.closest('.variable-button') !== null
    
    // If blur is from variable button or we just clicked it, don't process the blur
    if (isBlurFromVariableButton || isVariableButtonClicked) {
      setIsFocused(false)
      setIsVariableButtonClicked(false)
      return
    }
    
    setIsFocused(false)
    // Only notify parent blur if menu is not open and variable button wasn't clicked
    if (!showVariableMenu && !isVariableButtonClicked && onBlur) {
      // No onChange here; parent already has latest via onChange calls
      onBlur(e)
    }
    // Reset the flag after handling the blur
    setIsVariableButtonClicked(false)
  }

  const resolvedDateFormat = normalizeDynamicDateFormatPreset(dateFormat)

  // Generate preview text if the input contains dynamic date variables
  const previewText = React.useMemo(() => {
    if (!showPreview || !containsDynamicDateVariables(localValue)) return null
    return previewDynamicDateText(localValue, new Date(), resolvedDateFormat)
  }, [localValue, showPreview, resolvedDateFormat])

  // Check if we have any variables to show
  const hasExtractedVariables = extractedVariables.length > 0
  const hasFormFields = formFields.length > 0
  const hasDateVariables = showDateVariables && DYNAMIC_DATE_VARIABLES.length > 0
  const visibleTabCount = [
    hasExtractedVariables,
    hasFormFields,
    hasDateVariables,
    hasSystemVariables,
  ].filter(Boolean).length
  const shouldShowButton = visibleTabCount > 0
  const showTabs = visibleTabCount > 1

  const isDateListVisible =
    hasDateVariables && (activeTab === 'dates' || (!hasExtractedVariables && !hasFormFields && !hasSystemVariables))
  const variableSearchLabel = useMemo(() => {
    if (activeTab === 'form') return 'form'
    if (activeTab === 'system') return 'system'
    if (isDateListVisible) return 'date'
    return 'extracted'
  }, [activeTab, isDateListVisible])

  return (
    <div className={`enhanced-variable-reference-container ${className} relative`}>
      <div className="input-with-variable-button relative">
        {multiline ? (
          <textarea
            ref={inputRef}
            value={localValue}
            title={title ?? safeValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSelect={handleInputSelect}
            onClick={handleInputClick}
            onBlur={handleInputBlur}
            onFocus={() => setIsFocused(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={`variable-textarea enhanced-variable-textarea w-full resize-y text-sm border border-gray-300 rounded-md min-h-[72px] ${
              shouldShowButton ? 'pl-2 py-2' : 'p-2'
            }`}
            rows={rows}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={localValue}
            title={title ?? safeValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSelect={handleInputSelect}
            onClick={handleInputClick}
            onBlur={handleInputBlur}
            onFocus={() => setIsFocused(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={`variable-input enhanced-variable-input ${className} w-full text-sm border border-gray-300 rounded-md h-9 ${
              shouldShowButton ? 'pl-2 py-2' : 'p-2'
            }`}
          />
        )}
        
        {shouldShowButton && (
          <button
            type="button"
            disabled={disabled}
            className="variable-button absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 border-none bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white cursor-pointer rounded-md hover:shadow-md dark:hover:shadow-blue-900/30 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500 dark:disabled:hover:bg-blue-600"
            onClick={handleVariableButtonClick}
            title="Insert variable (Ctrl+{ for extracted, Ctrl+~ for dates, Ctrl+F for form fields)"
          >
            <Plus size={16} className="group-hover:scale-110 transition-transform duration-200" />
          </button>
        )}
      </div>

      {/* Variable Menu - positioned to overlay preview text */}
      {showVariableMenu && shouldShowButton && (
        <div 
          ref={menuRef}
          className="enhanced-variable-menu absolute top-full left-0 right-0 z-50 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl dark:shadow-2xl mt-1 max-h-96 backdrop-blur-sm dark:backdrop-blur-md"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
        >
          {/* Tabs */}
          {showTabs ? (
            <div className="variable-menu-tabs flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              {hasExtractedVariables && (
                <button
                  className={`tab-button flex-1 px-3 py-2.5 border-none text-sm font-medium cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-200 ${
                    activeTab === 'extracted' 
                      ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20' 
                      : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  onClick={() => {
                    setActiveTab('extracted')
                    setSearchTerm('')
                  }}
                >
                  <Database size={14} className="text-blue-500 dark:text-blue-400" />
                  Extracted Variables
                </button>
              )}
              {hasFormFields && (
                <button
                  className={`tab-button flex-1 px-3 py-2.5 border-none text-sm font-medium cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-200 ${
                    activeTab === 'form' 
                      ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20' 
                      : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  onClick={() => {
                    setActiveTab('form')
                    setSearchTerm('')
                  }}
                >
                  <FileText size={14} className="text-green-500 dark:text-green-400" />
                  Form Fields
                </button>
              )}
              {hasDateVariables && (
                <button
                  className={`tab-button flex-1 px-3 py-2.5 border-none text-sm font-medium cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-200 ${
                    activeTab === 'dates' 
                      ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20' 
                      : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  onClick={() => {
                    setActiveTab('dates')
                    setSearchTerm('')
                  }}
                >
                  <Calendar size={14} className="text-blue-500 dark:text-blue-400" />
                  Date Variables
                </button>
              )}
              {hasSystemVariables && (
                <button
                  className={`tab-button flex-1 px-3 py-2.5 border-none text-sm font-medium cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-200 ${
                    activeTab === 'system' 
                      ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20' 
                      : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  onClick={() => {
                    setActiveTab('system')
                    setSearchTerm('')
                  }}
                >
                  <User size={14} className="text-purple-500 dark:text-purple-400" />
                  System
                </button>
              )}
            </div>
          ) : null}
          
          {/* Search input */}
          <div className="variable-search p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30">
            <input
              type="text"
              placeholder={`Search ${variableSearchLabel} variables...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-200 dark:shadow-inner"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="max-h-72 overflow-y-auto bg-white dark:bg-gray-900 px-2 py-2">
            {/* Extracted Variables Tab */}
            {(activeTab === 'extracted' || (!hasDateVariables && !hasFormFields && !hasSystemVariables)) && hasExtractedVariables && (
              <>
                {filteredExtractedVariables.length > 0 ? (
                  filteredExtractedVariables.map((variable) => (
                    <button
                      key={variable.name}
                      className="variable-option w-full px-3 py-3 border-none bg-transparent text-left cursor-pointer border-b border-gray-100 dark:border-gray-800 transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-200 dark:hover:border-blue-800 group"
                      onClick={(e) => {
                        e.stopPropagation()
                        insertExtractedVariable(variable.name)
                      }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                      title={variable.name}
                    >
                      <div className="flex items-center gap-3">
                        <code className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-md font-mono border border-blue-200 dark:border-blue-800 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/60 transition-colors duration-200">
                          {`{{${variable.name}}}`}
                        </code>
                        <span className="variable-name text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-200">
                          {variable.name}
                        </span>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="no-variables-found p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                    <Database size={20} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <span>No extracted variables found</span>
                  </div>
                )}
              </>
            )}

            {/* Form Fields Tab */}
            {(activeTab === 'form' || (!hasExtractedVariables && !hasDateVariables && !hasSystemVariables)) && hasFormFields && (
              <>
                {Object.keys(filteredFormFields).length > 0 ? (
                  Object.keys(filteredFormFields).map(formSource => (
                    <div key={formSource} className="form-fields-group">
                      <div className="form-group-header px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 uppercase tracking-wide">
                        {formSource}
                      </div>
                      {filteredFormFields[formSource].map((field) => (
                        <button
                          key={`${formSource}-${field.name}`}
                          className="variable-option w-full px-3 py-3 border-none bg-transparent text-left cursor-pointer border-b border-gray-100 dark:border-gray-800 transition-all duration-200 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-200 dark:hover:border-green-800 group"
                          onClick={(e) => {
                            e.stopPropagation()
                            insertExtractedVariable(field.name)
                          }}
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                          title={`${field.label || field.name} · {{${field.name}}}`}
                        >
                          <div className="flex items-center gap-3">
                            <code className="text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 px-2 py-1 rounded-md font-mono border border-green-200 dark:border-green-800 group-hover:bg-green-200 dark:group-hover:bg-green-900/60 transition-colors duration-200">
                              {`{{${field.name}}}`}
                            </code>
                            <div className="flex-1">
                              <span className="variable-name text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-green-700 dark:group-hover:text-green-300 transition-colors duration-200">
                                {field.label || field.name}
                              </span>
                              {field.fieldType && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-200 mt-0.5">
                                  Type: {field.fieldType}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="no-variables-found p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                    <FileText size={20} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <span>No form fields found</span>
                  </div>
                )}
              </>
            )}

            {/* Date Variables Tab */}
            {(activeTab === 'dates' || (!hasExtractedVariables && !hasFormFields && !hasSystemVariables)) && hasDateVariables && (
              <>
                {typeof onDateFormatChange === 'function' && (
                  <div
                    className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/40"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1">
                      Date output format
                    </label>
                    <select
                      value={resolvedDateFormat}
                      onChange={(e) => {
                        const v = e.target.value
                        if (typeof onDateFormatChange === 'function') {
                          onDateFormatChange(v)
                        }
                      }}
                      className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 nodrag"
                    >
                      {DYNAMIC_DATE_FORMAT_PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                      Applies to ~date~ tokens in this step and server execution.
                    </p>
                  </div>
                )}
                {Object.keys(filteredDateCategories).length > 0 ? (
                  Object.keys(filteredDateCategories).map(categoryKey => {
                    const category = filteredDateCategories[categoryKey]
                    return (
                      <div key={categoryKey} className="date-category">
                        <div className="date-category-header px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 uppercase tracking-wide">
                          {category.label}
                        </div>
                        {category.variables.map(variable => (
                          <button
                            key={variable.key}
                            className="date-variable-option w-full px-3 py-3 border-none bg-transparent text-left cursor-pointer border-b border-gray-100 dark:border-gray-800 transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-200 dark:hover:border-blue-800 group"
                            onClick={(e) => {
                              e.stopPropagation()
                              insertDateVariable(variable.key)
                            }}
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                          >
                            <div className="flex items-center gap-3">
                              <code className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-md font-mono border border-blue-200 dark:border-blue-800 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/60 transition-colors duration-200">
                                ~{variable.key}~
                              </code>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-200">
                                  {variable.label}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 mt-0.5">
                                  {variable.description}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )
                  })
                ) : (
                  <div className="no-date-variables-found p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                    <Calendar size={20} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <span>No date variables found</span>
                  </div>
                )}
              </>
            )}

            {/* System Variables Tab */}
            {(activeTab === 'system' || (!hasExtractedVariables && !hasFormFields && !hasDateVariables)) && hasSystemVariables && (
              <>
                {Object.keys(filteredSystemVariablesByGroup).length > 0 ? (
                  Object.keys(filteredSystemVariablesByGroup).map(groupName => (
                    <div key={groupName} className="system-variables-group">
                      <div className="system-group-header px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 uppercase tracking-wide">
                        {groupName}
                      </div>
                      {filteredSystemVariablesByGroup[groupName].map((variable) => (
                        <button
                          key={variable.name}
                          className="variable-option w-full px-3 py-3 border-none bg-transparent text-left cursor-pointer border-b border-gray-100 dark:border-gray-800 transition-all duration-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-200 dark:hover:border-purple-800 group"
                          onClick={(e) => {
                            e.stopPropagation()
                            insertExtractedVariable(variable.name)
                          }}
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                          title={variable.description}
                        >
                          <div className="flex items-center gap-3">
                            <code className="text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 px-2 py-1 rounded-md font-mono border border-purple-200 dark:border-purple-800 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/60 transition-colors duration-200">
                              {`{{${variable.name}}}`}
                            </code>
                            <div className="flex-1">
                              <span className="variable-name text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors duration-200">
                                {variable.name}
                              </span>
                              <div className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200 mt-0.5">
                                {variable.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="no-variables-found p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                    <User size={20} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                    <span>No system variables found</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Preview text - appears below when menu is closed */}
      {previewText && !showVariableMenu && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-300 italic px-2 py-1 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 rounded-md">
          <Calendar size={12} className="inline mr-1 mb-0.5 text-blue-500 dark:text-blue-400" />
          Preview: {previewText}
        </div>
      )}
    </div>
  )
}

export default EnhancedVariableReference